import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { twoFactorAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Shield, ShieldCheck, ShieldOff, Copy, Check, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const TwoFactorSetup = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState({ enabled: false, backup_codes_remaining: 0 });
  const [loading, setLoading] = useState(true);
  const [setupData, setSetupData] = useState(null);
  const [showSetup, setShowSetup] = useState(false);
  const [showDisable, setShowDisable] = useState(false);
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const res = await twoFactorAPI.getStatus();
      setStatus(res.data);
    } catch (error) {
      console.error('Failed to load 2FA status');
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async () => {
    try {
      setLoading(true);
      const res = await twoFactorAPI.setup();
      setSetupData(res.data);
      setShowSetup(true);
    } catch (error) {
      toast.error('Error al configurar 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (code.length !== 6) {
      toast.error('El código debe tener 6 dígitos');
      return;
    }

    setVerifying(true);
    try {
      await twoFactorAPI.verify(code);
      toast.success('2FA activado correctamente');
      setShowSetup(false);
      setSetupData(null);
      setCode('');
      loadStatus();
    } catch (error) {
      toast.error('Código incorrecto');
    } finally {
      setVerifying(false);
    }
  };

  const handleDisable = async () => {
    if (code.length !== 6) {
      toast.error('El código debe tener 6 dígitos');
      return;
    }

    setVerifying(true);
    try {
      await twoFactorAPI.disable(code);
      toast.success('2FA desactivado');
      setShowDisable(false);
      setCode('');
      loadStatus();
    } catch (error) {
      toast.error('Código incorrecto');
    } finally {
      setVerifying(false);
    }
  };

  const copyBackupCodes = () => {
    if (setupData?.backup_codes) {
      navigator.clipboard.writeText(setupData.backup_codes.join('\n'));
      setCopied(true);
      toast.success('Códigos copiados');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <Card className="border-border rounded-sm shadow-none">
        <CardContent className="p-6 flex items-center justify-center">
          <RefreshCw className="w-6 h-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border-border rounded-sm shadow-none" data-testid="2fa-card">
        <CardHeader>
          <CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Shield size={18} className="text-primary" />
            Autenticación de Dos Factores (2FA)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                {status.enabled ? (
                  <>
                    <ShieldCheck className="text-emerald-500" size={20} />
                    <span className="font-medium text-foreground">2FA Activado</span>
                  </>
                ) : (
                  <>
                    <ShieldOff className="text-muted-foreground" size={20} />
                    <span className="font-medium text-foreground">2FA Desactivado</span>
                  </>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {status.enabled 
                  ? `${status.backup_codes_remaining} códigos de respaldo restantes`
                  : 'Añade una capa extra de seguridad a tu cuenta'
                }
              </p>
            </div>
            
            {status.enabled ? (
              <Button 
                variant="outline" 
                onClick={() => setShowDisable(true)}
                className="rounded-sm text-destructive border-destructive hover:bg-destructive/10"
              >
                Desactivar
              </Button>
            ) : (
              <Button 
                onClick={handleSetup}
                className="bg-primary hover:bg-primary/90 rounded-sm"
              >
                Activar 2FA
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Setup Dialog */}
      <Dialog open={showSetup} onOpenChange={setShowSetup}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle>Configurar 2FA</DialogTitle>
          </DialogHeader>
          
          {setupData && (
            <div className="space-y-6 py-4">
              {/* QR Code */}
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-4">
                  Escanea este código QR con tu app de autenticación
                </p>
                <div className="inline-block p-4 bg-white rounded-sm">
                  <img 
                    src={setupData.qr_code} 
                    alt="QR Code" 
                    className="w-48 h-48"
                  />
                </div>
              </div>
              
              {/* Manual entry */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Clave secreta (manual)</Label>
                <code className="block p-3 bg-muted rounded-sm text-xs font-mono break-all">
                  {setupData.secret}
                </code>
              </div>
              
              {/* Backup codes */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-muted-foreground">Códigos de respaldo</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={copyBackupCodes}
                    className="h-6 text-xs"
                  >
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                    <span className="ml-1">{copied ? 'Copiados' : 'Copiar'}</span>
                  </Button>
                </div>
                <div className="grid grid-cols-4 gap-2 p-3 bg-muted rounded-sm">
                  {setupData.backup_codes.map((code, i) => (
                    <code key={i} className="text-xs font-mono">{code}</code>
                  ))}
                </div>
                <p className="text-xs text-amber-600">
                  ⚠️ Guarda estos códigos en un lugar seguro. Los necesitarás si pierdes acceso a tu app.
                </p>
              </div>
              
              {/* Verification */}
              <div className="space-y-2">
                <Label>Introduce el código de tu app</Label>
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="rounded-sm font-mono text-center text-lg tracking-widest"
                  maxLength={6}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSetup(false)} className="rounded-sm">
              Cancelar
            </Button>
            <Button 
              onClick={handleVerify} 
              disabled={code.length !== 6 || verifying}
              className="bg-primary hover:bg-primary/90 rounded-sm"
            >
              {verifying ? 'Verificando...' : 'Verificar y Activar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disable Dialog */}
      <Dialog open={showDisable} onOpenChange={setShowDisable}>
        <DialogContent className="rounded-sm max-w-sm">
          <DialogHeader>
            <DialogTitle>Desactivar 2FA</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Para desactivar 2FA, introduce el código actual de tu app de autenticación.
            </p>
            
            <div className="space-y-2">
              <Label>Código de verificación</Label>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="rounded-sm font-mono text-center text-lg tracking-widest"
                maxLength={6}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDisable(false)} className="rounded-sm">
              Cancelar
            </Button>
            <Button 
              onClick={handleDisable} 
              disabled={code.length !== 6 || verifying}
              variant="destructive"
              className="rounded-sm"
            >
              {verifying ? 'Verificando...' : 'Desactivar 2FA'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TwoFactorSetup;
